/** @jsxImportSource preact */
import { h, render } from "preact";

import {
  createConversation,
  type Conversation,
  type ConversationConfig,
} from "./conversation/conversation";
import type { ProactivePrompt } from "./proactive/proactive";
import { EmbeddedRoot, WidgetRoot } from "./widget-root";

export interface ChatMountInput {
  /** 每次重画都重读：主题编辑器的定位覆盖和 Playground 明暗切换会变。 */
  readConfig: () => ConversationConfig;
  /** 主动提示由入口阶段创建；这里只消费，不重建规则。 */
  getPrompt: () => ProactivePrompt | undefined;
  /** 会话状态变化先交给主动规则（频控、抑制），再重画。 */
  onConversationChange: () => void;
}

export interface ChatMountHandle {
  conversation: Conversation;
  /** 外观或提示变了，入口侧调它重画。 */
  redraw: () => void;
  dispose: () => void;
}

/** widget-chat.js 执行后挂在全局的模块形状。 */
export interface ChatModule {
  mountChat: typeof mountChat;
}

/**
 * 完整聊天资源的挂载点：到这里才建会话、补回待答、渲染 WidgetRoot。
 * 入口阶段只攒意图；买家身份和 Socket 照旧在第一次发送时才建。
 */
export function mountChat(
  root: HTMLElement,
  input: ChatMountInput,
): ChatMountHandle {
  const conversation = createConversation(input.readConfig(), () => {
    input.onConversationChange();
    draw();
  });
  const draw = () => {
    const config = input.readConfig();
    render(
      h(config.playgroundSession ? EmbeddedRoot : WidgetRoot, {
        config,
        conversation,
        state: conversation.getState(),
        proactive: input.getPrompt(),
      }),
      root,
    );
  };
  draw();
  void conversation.resume();
  return {
    conversation,
    redraw: draw,
    dispose() {
      conversation.dispose();
      render(null, root);
    },
  };
}
